import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface FavoriteToggleResult {
	menuItemKey: string;
	label?: string;
	/** Whether the request succeeded. */
	success: boolean;
	/** Resulting state on success; the state that was attempted on failure. */
	favorited: boolean;
}

interface FavoriteMenuItemsResponse {
	data: string[];
}

interface ToggleFavoriteMenuItemResponse {
	message: string;
	action: 'added' | 'removed';
	menu_item_key: string;
	is_favorite: boolean;
}

@Injectable({ providedIn: 'root' })
export class FavoritesService {
	private baseUrl = environment.apiUrl;

	private favoriteKeysSubject = new BehaviorSubject<string[]>([]);
	favoriteKeys$: Observable<string[]> = this.favoriteKeysSubject.asObservable();

	/**
	 * Emits the outcome of each toggleFavorite() call so a component can surface
	 * a toast. Toasting can't happen in this service: MessageService is provided
	 * on AppComponent, not in root, so a root-provided service either can't
	 * inject it or would get a separate instance whose messages never reach the
	 * <p-toast> in app.component.html. Subscribe from a single, long-lived
	 * component (AppMenuComponent) - subscribing per menu item would fire one
	 * duplicate toast per item in the sidebar.
	 */
	private toggleResultSubject = new Subject<FavoriteToggleResult>();
	toggleResult$: Observable<FavoriteToggleResult> = this.toggleResultSubject.asObservable();

	/**
	 * Emits when loadFavorites() fails, so a component can surface a toast (same
	 * DI reasoning as toggleResult$ above). The sidebar still renders without a
	 * My Favorites list in this case - this only tells the user why it's empty.
	 */
	private loadErrorSubject = new Subject<void>();
	loadError$: Observable<void> = this.loadErrorSubject.asObservable();

	/**
	 * False until the first loadFavorites() call settles. favoriteKeys$ starts as
	 * an empty array, so without this there is no way to tell "this user has no
	 * favorites" apart from "favorites haven't arrived yet" - and on a slow
	 * connection that difference matters: an already-favorited item would render
	 * an unfilled star, and clicking it would send a toggle that un-favorites it.
	 */
	private loadedSubject = new BehaviorSubject<boolean>(false);
	loaded$: Observable<boolean> = this.loadedSubject.asObservable();

	get isLoaded(): boolean {
		return this.loadedSubject.value;
	}

	constructor(private http: HttpClient) {}

	/**
	 * Fetches the authenticated user's favorited menu item keys from the backend
	 * and publishes them via favoriteKeys$. Safe to call multiple times (e.g. on
	 * login) - each call replaces the current list once the response arrives.
	 */
	loadFavorites(): void {
		this.http.get<FavoriteMenuItemsResponse>(`${this.baseUrl}favorite-menu-items`).subscribe({
			next: (response) => {
				// loaded before keys: emitting keys triggers a menu rebuild, which
				// reads isLoaded to decide whether favorites UI can be shown.
				this.loadedSubject.next(true);
				this.favoriteKeysSubject.next(Array.isArray(response?.data) ? response.data : []);
			},
			error: () => {
				// Leave favorites empty rather than throwing - the sidebar should
				// still render (just without a My Favorites list) if this fails.
				// Still mark as loaded so the UI settles instead of hanging in a
				// permanent loading state; the warning toast explains the gap.
				this.loadedSubject.next(true);
				this.favoriteKeysSubject.next([]);
				this.loadErrorSubject.next();
			}
		});
	}

	/**
	 * Toggles the favorite state for a menu item key for the authenticated user.
	 * Updates local state optimistically, then reconciles/rolls back based on the
	 * server response.
	 */
	toggleFavorite(menuItemKey: string, label?: string): void {
		if (!menuItemKey) {
			return;
		}

		// Defensive: the star is hidden until favorites load, but if a toggle did
		// slip through beforehand we'd be deciding add-vs-remove from an empty
		// list and could silently invert the user's intent.
		if (!this.isLoaded) {
			return;
		}

		const previousKeys = this.favoriteKeysSubject.value;
		const wasFavorite = previousKeys.includes(menuItemKey);
		const optimisticKeys = wasFavorite
			? previousKeys.filter((key) => key !== menuItemKey)
			: [...previousKeys, menuItemKey];

		this.favoriteKeysSubject.next(optimisticKeys);

		this.http
			.post<ToggleFavoriteMenuItemResponse>(
				`${this.baseUrl}favorite-menu-items/${encodeURIComponent(menuItemKey)}`,
				{}
			)
			.subscribe({
				next: (response) => {
					const favorited = typeof response?.is_favorite === 'boolean' ? response.is_favorite : !wasFavorite;

					const currentKeys = this.favoriteKeysSubject.value;
					const isCurrentlyFavorite = currentKeys.includes(menuItemKey);

					if (favorited && !isCurrentlyFavorite) {
						this.favoriteKeysSubject.next([...currentKeys, menuItemKey]);
					} else if (!favorited && isCurrentlyFavorite) {
						this.favoriteKeysSubject.next(currentKeys.filter((key) => key !== menuItemKey));
					}

					this.toggleResultSubject.next({ menuItemKey, label, success: true, favorited });
				},
				error: () => {
					// Roll back the optimistic update on failure.
					this.favoriteKeysSubject.next(previousKeys);
					this.toggleResultSubject.next({
						menuItemKey,
						label,
						success: false,
						favorited: !wasFavorite
					});
				}
			});
	}

	/**
	 * Synchronous helper for template use - checks the current in-memory
	 * favorites list without subscribing.
	 */
	isFavorite(menuItemKey: string | null | undefined): boolean {
		if (!menuItemKey) {
			return false;
		}

		return this.favoriteKeysSubject.value.includes(menuItemKey);
	}
}
